import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { InfoSet } from "../InfoSets/InfoSet";
import { IpAddress, IpVersion } from "../Core/IpAddress";
import { MacAddress } from "../Core/MacAddress";
import { PortList } from "../Core/PortList";
import { Device } from "../Core/Device";
import { Dictionary } from "../Utils/Dictionary";
import { IfEntry, IfTable } from "../InfoSets/IfTable";
import { List } from "../Utils/List";
import { HashSet } from "../Utils/HashSet";
import { BridgePortEntry, BridgePortTable } from "../InfoSets/BridgePortTable";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class LagEntry implements ObjectSerializer {
	public Index: number;

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public MacAddress: MacAddress;
	public get HasMacAddress(): boolean {
		return this.MacAddress && !this.MacAddress.IsNull;
	}

	public ActorSystemID: MacAddress;
	public get HasActorSystemID(): boolean {
		return this.ActorSystemID && !this.ActorSystemID.IsNull;
	}

	public PartnerSystemID: MacAddress;
	public get HasPartnerSystemID(): boolean {
		return this.PartnerSystemID && !this.PartnerSystemID.IsNull;
	}

	public Ports: PortList;
	public get HasPorts(): boolean {
		return this.Ports && this.Ports.HasPorts;
	}

	constructor(lagIndex?: number) {
		this.Index = lagIndex;
		this.Name = '';

		this.MacAddress = null;
		this.ActorSystemID = null;
		this.PartnerSystemID = null;

		this.Ports = new PortList();
	}

	public GetBaseIfIndex(): number {
		return this.Ports.BasePortIndex;
	}

	public Write(obj: any, context: any): void {
		obj.lag_entry = {};
		obj = obj.lag_entry;

		obj.index = ValueEncoder.EncodeInteger(this.Index);

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasMacAddress)
			obj.mac_address = ValueEncoder.EncodeMacAddress(this.MacAddress);

		if (this.HasActorSystemID)
			obj.actor_system_id = ValueEncoder.EncodeMacAddress(this.ActorSystemID);

		if (this.HasPartnerSystemID)
			obj.partner_system_id = ValueEncoder.EncodeMacAddress(this.PartnerSystemID);

		obj.ports = this.Ports.ToHexString();
	}

	public Read(obj: any, context: any): void {
		if (!obj.lag_entry)
			throw new Error();
		obj = obj.lag_entry;

		if (obj.index !== undefined)
			this.Index = ValueEncoder.DecodeInteger(obj.index);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.mac_address !== undefined)
			this.MacAddress = ValueEncoder.DecodeMacAddress(obj.mac_address);

		if (obj.actor_system_id !== undefined)
			this.ActorSystemID = ValueEncoder.DecodeMacAddress(obj.actor_system_id);

		if (obj.partner_system_id !== undefined)
			this.PartnerSystemID = ValueEncoder.DecodeMacAddress(obj.partner_system_id);

		if (obj.ports !== undefined)
			this.Ports.SetPorts(obj.ports);
	}
}

export class LagTable extends InfoSet
{
	private _lagTable: Dictionary<number, LagEntry> ;

	constructor(device: Device) {
		super(device);
		this._lagTable = new Dictionary<number, LagEntry>();
	}

	public Add(lagIndex:number): LagEntry {
		var lagEntry: LagEntry = null;

		var lagEntry:LagEntry = this._lagTable.Item(lagIndex);
		if(!lagEntry) {
			lagEntry = new LagEntry(lagIndex);
			this._lagTable.Add(lagIndex, lagEntry);
		}

		return lagEntry;
	}

	private AddEntry(lagEntry: LagEntry): void {
		this._lagTable.Add(lagEntry.Index, lagEntry);
	}

	public Clear(): void {
		this._lagTable.Clear();
	}

	public get Count():number {
		return this._lagTable.Count; 
	}

	public Find(lagIndex:number): LagEntry {
		var foundLagEntry: LagEntry = null;
		this._lagTable.Some((pair) => {
			if (pair.Value.Index != lagIndex)
				return;

			foundLagEntry = pair.Value;
			return true;
		});

		return foundLagEntry;
	}

	public FindLagContaining(ifIndex:number): LagEntry	{
		var foundLagEntry: LagEntry = null;

		if (this.Device.HasIfTable) {
			var ifTable: IfTable = this.Device.IfTable;
			var ifEntry: IfEntry = null;

			this._lagTable.Some((pair) => {
				var lagEntry: LagEntry = pair.Value;
				// Looking for LAGs that CONTAIN ports ONLY!!!
				if (!lagEntry.HasPorts)
					return;

				// CHECK MAIN index
				ifEntry = ifTable.Find(lagEntry.Index);
				if (ifEntry != null) {
					ifEntry = ifEntry.PhysicalIfEntry;
					if (ifEntry != null) {
						if (ifEntry.IfIndex == ifIndex) {
							foundLagEntry = lagEntry;
							return true;
						}
					}
				}

				// CHECK CHILDREN
				return lagEntry.Ports.ToPortList().some((portIndex) => {
					ifEntry = ifTable.Find(portIndex);
					if (ifEntry == null)
						return;

					ifEntry = ifEntry.PhysicalIfEntry;
					if (ifEntry == null)
						return;

					if (ifEntry.IfIndex != ifIndex)
						return;

					foundLagEntry = lagEntry;
					return true;
				});
			});
		}

		return foundLagEntry;

	}

	public Remove(lagIndex:number):boolean {
		return this._lagTable.Remove(lagIndex);
	}

	public RemoveEntry(lagEntry: LagEntry):boolean {
		return lagEntry ? this._lagTable.Remove(lagEntry.Index) : false;
	}

	public RemoveEmpty(): void {
		var emptyLagPorts = new List<number>();

		this._lagTable.ForEach((pair) => {
			if (pair.Value.HasPorts)
				return;
			emptyLagPorts.Add(pair.Value.Index);
		});

		if (emptyLagPorts.Count > 0) {
			emptyLagPorts.ForEach((lagPortIndex) => {
				this.Remove(lagPortIndex);
			});
		}
	}

	public get HasMacAddresses(): boolean {
		return true;
	}

	public get MacAddresses(): HashSet<MacAddress> {
		var macAddresses = new HashSet<MacAddress>();

		this._lagTable.ForEach((pair) => {
			var lagEntry: LagEntry = pair.Value;
			if (!lagEntry.HasActorSystemID)
				return;

			if (lagEntry.ActorSystemID.IsNull)
				return;

			macAddresses.Add(lagEntry.ActorSystemID);
		});

		return (macAddresses);
	}

	public VerifyLagPorts():void {
		if (this.Device == null)
			return;
		if (!this.Device.HasIfTable)
			return;
		if (!this.Device.HasBridgePortTable)
			return;

		var ifTable: IfTable = this.Device.IfTable;
		var ifEntry: IfEntry = null;
		var bpTable: BridgePortTable = this.Device.BridgePortTable;
		var bpEntry: BridgePortEntry = null;

		// REMOVE Empty LAG entries
		this.RemoveEmpty();

		// VERIFY / BUILD BridgePorts to match LAG configuration
		this._lagTable.ForEach((pair) => {
			if (!pair.Value.HasPorts)
				return;

			pair.Value.Ports.ToPortList().forEach((lagBpIndex) => {
				bpEntry = bpTable.Find(lagBpIndex);
				if (bpEntry != null)
					return;

				ifEntry = ifTable.Find(lagBpIndex);
				if (ifEntry == null)
					return;

				if (ifEntry.HasBridgePortIndex)
					return;

				bpEntry = bpTable.Add(lagBpIndex);
				{
					bpEntry.IfIndex = lagBpIndex;
					ifEntry.BridgePortIndex = lagBpIndex;
				}
			});
		});
	}

	public ForEach(callbackfn: (value: LagEntry, index: number, array: LagEntry[]) => void, thisArg?: any): void {
		this._lagTable.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: LagEntry, index: number, array: LagEntry[]) => boolean, thisArg?: any): boolean {
		return this._lagTable.Values().Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: LagEntry, index: number, array: LagEntry[]) => boolean, thisArg?: any): boolean {
		return this._lagTable.Values().Every(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		obj.lag_info = {};
		obj = obj.lag_info;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._lagTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.lag_info)
			throw new Error();
		obj = obj.lag_info;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new LagEntry();
				entry.Read(value, context);
				this.AddEntry(entry);
			}, this);
		}
	}
}
